import { IApiBaseProps } from '@common/api.models';
import { isValidApiBaseProps } from '@common/api.services';
import { FETCH_QUERIES_NAME, MUTATION_KEYS } from '@common/constants';
import { createUseFetchSearchFn } from '@common/search.services';
import { AxiosErrorResponse } from '@core/error.models';
import { isPositiveNumber } from '@core/Primitives/number.services';
import { IItemResponse } from '@gcl/react-core';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  ICreateProjectLoadingCertRequest,
  IProjectLoadingRemovalCert,
  IProjectLoadingRemovalCertT3,
  IProjectLoadingRemovalIceCertRequestProps,
  IUpdateProjectLoadingRemovalCertRequest,
} from './detail/permitToLoadRemoveDetailPage.models';
import { ILoadingRemovalCertSearch, ILoadingRemovalCertSearchFilter } from './permitToLoadRemove.models';
import {
  deleteLoadingRemovalCertAsync,
  getLoadingRemovalCertAsync,
  getLoadingRemovalCertCheckListAsync,
  getLoadingRemovalCertDrawingsAsync,
  getLoadingRemovalCertT3Async,
  postLoadingRemovalCertAsync,
  postLoadingRemovalCertCreateAndIssueAsync,
  postLoadingRemovalCertRemovalCertsAsync,
  postLoadingRemovalCertRevisionsAsync,
  putLoadingRemovalCertAsync,
  putLoadingRemovalCertIceApprovedAsync,
  putLoadingRemovalCertIceCertRequestAsync,
  putLoadingRemovalCertSrpIssuedAsync,
  putLoadingRemovalCertTwcApprovedAsync,
  searchLoadingRemovalCertAsync,
} from './permitToLoadRemove.services';

export const useFetchLoadingRemovalCertSearch = createUseFetchSearchFn<
  ILoadingRemovalCertSearchFilter,
  ILoadingRemovalCertSearch
>({
  queryName: FETCH_QUERIES_NAME.LOADING_REMOVAL_CERT_SEARCH,
  searchAsyncFn: searchLoadingRemovalCertAsync,
});

export const useFetchLoadingRemovalCert = (props: IApiBaseProps, id?: number, enabled = true) => {
  return useQuery<IProjectLoadingRemovalCert | undefined, AxiosErrorResponse>(
    [props.projectId, id, FETCH_QUERIES_NAME.LOADING_REMOVAL_CERT_DETAIL],
    async () => {
      if (!id) {
        return undefined;
      }
      const results = await getLoadingRemovalCertAsync(id);
      return results.item;
    },
    {
      enabled: isPositiveNumber(id) && isValidApiBaseProps(props) && enabled,
    },
  );
};

interface ICreateLoadingRemovalCertProps {
  createRequest: ICreateProjectLoadingCertRequest;
  type: 'create' | 'createAndIssue';
}
export const useCreateLoadingRemovalCertAsync = () => {
  return useMutation<
    IItemResponse<IProjectLoadingRemovalCert>,
    AxiosErrorResponse,
    ICreateLoadingRemovalCertProps,
    unknown
  >([MUTATION_KEYS.CREATE_LOADING_REMOVAL_CERT], async ({ createRequest, type }: ICreateLoadingRemovalCertProps) => {
    if (type === 'createAndIssue') {
      const result = await postLoadingRemovalCertCreateAndIssueAsync(createRequest);
      return result;
    }
    const result = await postLoadingRemovalCertAsync(createRequest);
    return result;
  });
};

export const useUpdateLoadingRemovalCertAsync = () => {
  return useMutation<
    IItemResponse<IProjectLoadingRemovalCert>,
    AxiosErrorResponse,
    IUpdateProjectLoadingRemovalCertRequest,
    unknown
  >([MUTATION_KEYS.UPDATE_LOADING_REMOVAL_CERT], async (updateRequest: IUpdateProjectLoadingRemovalCertRequest) => {
    const result = await putLoadingRemovalCertAsync(updateRequest.id, updateRequest);
    return result;
  });
};

export const useDeleteLoadingRemovalCertAsync = () => {
  return useMutation<boolean, AxiosErrorResponse, number, unknown>(
    [MUTATION_KEYS.DELETE_LOADING_REMOVAL_CERT],
    async (id: number) => {
      const result = await deleteLoadingRemovalCertAsync(id);
      return result;
    },
  );
};

export const useCreateLoadingRemovalCertRevisionAsync = () => {
  return useMutation<IItemResponse<IProjectLoadingRemovalCert>, AxiosErrorResponse, number, unknown>(
    [MUTATION_KEYS.CREATE_LOADING_REMOVAL_CERT_REVISION],
    async (id: number) => {
      const result = await postLoadingRemovalCertRevisionsAsync(id);
      return result;
    },
  );
};

export const useCreateLoadingRemovalCertRemovalCertAsync = () => {
  return useMutation<IItemResponse<IProjectLoadingRemovalCert>, AxiosErrorResponse, number, unknown>(
    [MUTATION_KEYS.CREATE_LOADING_REMOVAL_CERT_REMOVAL_CERT],
    async (id: number) => {
      const result = await postLoadingRemovalCertRemovalCertsAsync(id);
      return result;
    },
  );
};

export const useRequestLoadingRemovalCertIceCertAsync = () => {
  return useMutation<boolean, AxiosErrorResponse, IProjectLoadingRemovalIceCertRequestProps & { id: number }, unknown>(
    [MUTATION_KEYS.REQUEST_LOADING_REMOVAL_CERT_ICE_CERT],
    async ({ id, ...rest }: IProjectLoadingRemovalIceCertRequestProps & { id: number }) => {
      const result = await putLoadingRemovalCertIceCertRequestAsync(id, rest);
      return result;
    },
  );
};

export const useIssueLoadingRemovalCertRemovalCertSrpAsync = () => {
  return useMutation<
    IItemResponse<IProjectLoadingRemovalCert>,
    AxiosErrorResponse,
    IUpdateProjectLoadingRemovalCertRequest,
    unknown
  >([MUTATION_KEYS.ISSUE_LOADING_REMOVAL_CERT_SRP], async (props: IUpdateProjectLoadingRemovalCertRequest) => {
    const result = await putLoadingRemovalCertSrpIssuedAsync(props.id, props);
    return result;
  });
};
export const useApproveLoadingRemovalCertRemovalCertTwcAsync = () => {
  return useMutation<
    IItemResponse<IProjectLoadingRemovalCert>,
    AxiosErrorResponse,
    IUpdateProjectLoadingRemovalCertRequest,
    unknown
  >([MUTATION_KEYS.APPROVE_LOADING_REMOVAL_CERT_TWC], async (props: IUpdateProjectLoadingRemovalCertRequest) => {
    const result = await putLoadingRemovalCertTwcApprovedAsync(props.id, props);
    return result;
  });
};
export const useApproveLoadingRemovalCertRemovalCertIceAsync = () => {
  return useMutation<
    IItemResponse<IProjectLoadingRemovalCert>,
    AxiosErrorResponse,
    IUpdateProjectLoadingRemovalCertRequest,
    unknown
  >([MUTATION_KEYS.APPROVE_LOADING_REMOVAL_CERT_ICE], async (props: IUpdateProjectLoadingRemovalCertRequest) => {
    const result = await putLoadingRemovalCertIceApprovedAsync(props.id, props);
    return result;
  });
};

export const useFetchLoadingRemovalCertDrawings = (props: IApiBaseProps, id?: number, enabled = true) => {
  return useQuery(
    [props.projectId, id, FETCH_QUERIES_NAME.LOADING_REMOVAL_CERT_DRAWINGS],
    async () => {
      if (!id) {
        return undefined;
      }
      const results = await getLoadingRemovalCertDrawingsAsync(id);
      return results.items;
    },
    {
      enabled: isPositiveNumber(id) && isValidApiBaseProps(props) && enabled,
    },
  );
};
export const useFetchLoadingRemovalCertT3 = (props: IApiBaseProps, id?: number, enabled = true) => {
  return useQuery<IProjectLoadingRemovalCertT3[] | undefined, AxiosErrorResponse>(
    [props.projectId, id, FETCH_QUERIES_NAME.LOADING_REMOVAL_CERT_T3],
    async () => {
      if (!id) {
        return undefined;
      }
      const results = await getLoadingRemovalCertT3Async(id);
      return results.items;
    },
    {
      enabled: isPositiveNumber(id) && isValidApiBaseProps(props) && enabled,
    },
  );
};

export const useFetchLoadingRemovalCertCheckList = (props: IApiBaseProps, id?: number, enabled = true) => {
  return useQuery(
    [props.projectId, id, FETCH_QUERIES_NAME.LOADING_REMOVAL_CERT_CHECKLIST],
    async () => {
      if (!id) {
        return undefined;
      }
      const results = await getLoadingRemovalCertCheckListAsync(id, props.projectId);
      return results.items;
    },
    {
      enabled: isPositiveNumber(id) && isValidApiBaseProps(props) && enabled,
    },
  );
};
